import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Switch,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Popconfirm,
  Alert,
  Divider,
  Tag,
  Collapse,
  Grid,
  Tabs,
  Row,
  Col,
  Statistic,
  DatePicker,
  Drawer,
  Tooltip,
  Empty,
  Spin,
} from 'antd';
import {
  SafetyOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  FilterOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import { CountryLabel, CountryFlagList } from '../../../shared/components/CountryFlag';
import {
  WAF_RULESETS,
  CATEGORY_NAMES,
  CATEGORY_ICONS,
  SEVERITY_COLORS,
  SEVERITY_NAMES
} from '../../../shared/constants/wafRules';
import dayjs from 'dayjs';
import * as echarts from 'echarts';

const { Panel } = Collapse;
const { useBreakpoint } = Grid;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// 动作颜色映射
const ACTION_COLORS = {
  block: 'red',
  allow: 'green',
  js_challenge: 'orange',
  managed_challenge: 'gold',
  log: 'blue',
  ratelimit: 'purple',
};

const ACTION_LABELS = {
  block: '拦截',
  allow: '放行',
  js_challenge: 'JS 质询',
  managed_challenge: '托管质询',
  log: '仅记录',
  ratelimit: '速率限制',
};

// 可用的数据集合
const COLLECTIONS = [
  { value: 'IP', label: 'IP 地址' },
  { value: 'COUNTRY', label: '国家代码' },
  { value: 'ASN', label: 'ASN' },
  { value: 'URI', label: 'URI 路径' },
  { value: 'HOST', label: '主机名' },
  { value: 'METHOD', label: '请求方法' },
  { value: 'USER_AGENT', label: 'User-Agent' },
  { value: 'REFERER', label: 'Referer' },
  { value: 'HEADER', label: '请求头' },
  { value: 'COOKIE', label: 'Cookie' },
  { value: 'QS_ARGS', label: '查询参数' },
  { value: 'PATH', label: '路径' },
  { value: 'EXTENSION', label: '扩展名' },
  { value: 'PROTOCOL', label: '协议' },
  { value: 'JA3', label: 'JA3 指纹' },
  { value: 'JA4', label: 'JA4 指纹' },
  { value: 'H2', label: 'HTTP/2 指纹' },
];

// 可用的运算符
const OPERATORS = [
  { value: 'eq', label: '等于' },
  { value: 'neq', label: '不等于' },
  { value: 'contain', label: '包含' },
  { value: 'not_contain', label: '不包含' },
  { value: 'starts_with', label: '以...开头' },
  { value: 'ends_with', label: '以...结尾' },
  { value: 'in', label: '在列表中' },
  { value: 'not_in', label: '不在列表中' },
  { value: 'rx', label: '正则匹配' },
  { value: 'gt', label: '大于' },
  { value: 'lt', label: '小于' },
  { value: 'gte', label: '大于等于' },
  { value: 'lte', label: '小于等于' },
  { value: 'exists', label: '存在' },
  { value: 'not_exists', label: '不存在' },
];

// 自定义 WAF 规则（cdn_waf_config.custom_rules）—— CRS 风格评分规则，
// 与上方通用「防火墙规则」(firewall_rules 表) 分离：这里的规则命中后按分值累加异常分，
// 达到异常分阈值即拦截，结构对齐 waf_rulesets.rules 与 Rust wafcrs 引擎。
const WAF_MATCH_COLLECTIONS = [
  { value: 'URI', label: 'URI（含查询串）' },
  { value: 'PATH', label: 'PATH（路径）' },
  { value: 'QS_ARGS', label: '查询参数' },
  { value: 'BODY', label: '请求体' },
  { value: 'HEADER', label: '请求头' },
  { value: 'COOKIE', label: 'Cookie' },
  { value: 'USER_AGENT', label: 'User-Agent' },
  { value: 'METHOD', label: '请求方法' },
];

const WAF_MATCH_TYPES = [
  { value: 'regex', label: '正则匹配' },
  { value: 'detect_sqli', label: 'SQL injection (smart detection)' },
  { value: 'detect_xss', label: 'XSS (smart detection)' },
];

const WAF_CUSTOM_ACTIONS = [
  { value: 'block', label: '拦截' },
  { value: 'log', label: '仅记录（计分）' },
];

export default function Firewall() {
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [activeTab, setActiveTab] = useState('rules');

  // WAF 预设规则状态
  const [loading, setLoading] = useState(false);
  const [wafEnabled, setWafEnabled] = useState(false);
  const [wafConfig, setWafConfig] = useState({});
  const [enabledRulesets, setEnabledRulesets] = useState(['sqli', 'xss']);
  const [rulesetsDirty, setRulesetsDirty] = useState(false);
  const [availableRulesets, setAvailableRulesets] = useState({});
  const [rulesetsVersion, setRulesetsVersion] = useState(1);

  // 自定义 WAF 评分规则（custom_rules）状态
  const [customRules, setCustomRules] = useState([]);
  const [crModalVisible, setCrModalVisible] = useState(false);
  const [editingCrIndex, setEditingCrIndex] = useState(null);
  const [crSaving, setCrSaving] = useState(false);
  const [wafToggling, setWafToggling] = useState(false);
  const [crForm] = Form.useForm();

  // 防火墙规则状态
  const [fwRules, setFwRules] = useState([]);
  const [fwLoading, setFwLoading] = useState(false);
  const [fwModalVisible, setFwModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [fwForm] = Form.useForm();

  // 安全区（专用防盗链 / 地区封禁 / UA 名单 + 访问鉴权）状态
  const [secForm] = Form.useForm();
  const [secSaving, setSecSaving] = useState(false);
  const authMethodsWatch = Form.useWatch(['auth', 'methods'], secForm);
  const uamModeWatch = Form.useWatch(['uam', 'mode'], secForm);

  // WAF 分析状态
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsRange, setAnalyticsRange] = useState([dayjs().subtract(24, 'hour'), dayjs()]);
  const [overview, setOverview] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [topRules, setTopRules] = useState([]);
  const [topIPs, setTopIPs] = useState([]);
  const timelineChartRef = useRef(null);
  const timelineChartInstance = useRef(null);

  // 事件日志状态
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsRange, setLogsRange] = useState([dayjs().subtract(24, 'hour'), dayjs()]);
  const [logs, setLogs] = useState([]);
  const [logFilters, setLogFilters] = useState({});
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [logsTotal, setLogsTotal] = useState(0);

  useEffect(() => {
    if (domainData) {
      setWafEnabled(!!domainData.waf_enabled);
      loadWafRulesets();
      loadWafConfig();
      loadFirewallRules();
      secForm.setFieldsValue(securityToForm(domainData?.settings?.security));
    }
  }, [domainData]);

  useEffect(() => {
    if (activeTab === 'analytics' && domainData) {
      loadAnalytics();
    }
  }, [activeTab, analyticsRange]);

  useEffect(() => {
    if (activeTab === 'logs' && domainData) {
      loadLogs();
    }
  }, [activeTab, logsRange, logFilters, logsPage, logsPageSize]);

  // 过滤条件或时间范围变化时重置到第一页
  useEffect(() => {
    setLogsPage(1);
  }, [logsRange, logFilters]);

  // 时间线图表
  useEffect(() => {
    let cleanupResize;
    if (timeline.length > 0 && timelineChartRef.current) {
      cleanupResize = renderTimelineChart();
    }
    return () => {
      if (cleanupResize) {
        cleanupResize();
      }
      if (timelineChartInstance.current) {
        timelineChartInstance.current.dispose();
      }
    };
  }, [timeline]);

  // ===================== WAF 预设规则 =====================

  const loadWafRulesets = async () => {
    try {
      const response = await cdnService.getWafRulesetsGrouped();
      if (response.success && response.data) {
        const rulesetsMap = {};
        Object.entries(response.data).forEach(([category, rulesets]) => {
          rulesets.forEach(ruleset => {
            rulesetsMap[ruleset.key] = { ...ruleset };
          });
        });
        setAvailableRulesets(rulesetsMap);
        setRulesetsVersion(response.version || 1);
      } else {
        setAvailableRulesets(WAF_RULESETS);
      }
    } catch (error) {
      setAvailableRulesets(WAF_RULESETS);
    }
  };

  const loadWafConfig = async () => {
    setLoading(true);
    try {
      const response = await cdnService.getWafConfig(domainData.id);
      if (response.success && response.data) {
        setEnabledRulesets(response.data.enabled_rulesets || ['sqli', 'xss']);
        setCustomRules(Array.isArray(response.data.custom_rules) ? response.data.custom_rules : []);
        setWafConfig(response.data);
        setRulesetsDirty(false);
      }
    } catch (error) {
      console.error('Failed to load WAF config:', error);
      message.error('WAF 配置加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleWafToggle = async (checked) => {
    if (wafToggling) return;
    setWafToggling(true);
    try {
      const response = await cdnService.updateDomain({
        id: domainData.id,
        waf_enabled: !!checked
      });
      if (response.success) {
        setWafEnabled(checked);
        message.success(checked ? 'WAF已启用' : 'WAF已禁用');
        reloadDomain();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    } finally {
      setWafToggling(false);
    }
  };

  const handleRulesetToggle = (rulesetKey, checked) => {
    setEnabledRulesets(checked
      ? [...enabledRulesets, rulesetKey]
      : enabledRulesets.filter(key => key !== rulesetKey));
    setRulesetsDirty(true);
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      const response = await cdnService.saveWafConfig({
        domain_id: domainData.id,
        enabled_rulesets: enabledRulesets,
        mode: wafConfig.mode || 'active',
        score_threshold: wafConfig.score_threshold || 5,
        // CRS 规则集开关：paranoia level（1-4）+ 异常分阈值，对齐 Rust wafcrs CrsConfig。
        paranoia_level: wafConfig.paranoia_level || 1,
        // 自定义评分规则一并回写，避免保存规则集时把已存的 custom_rules 清空。
        custom_rules: customRules,
      });
      if (response.success) {
        message.success('WAF配置已保存');
        loadWafConfig();
      } else {
        message.error(response.error || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const rulesetsByCategory = React.useMemo(() => {
    const grouped = {};
    Object.entries(availableRulesets).forEach(([key, ruleset]) => {
      if (!grouped[ruleset.category]) grouped[ruleset.category] = [];
      grouped[ruleset.category].push({ key, ...ruleset });
    });
    return grouped;
  }, [availableRulesets]);

  // ===================== 自定义 WAF 评分规则 CRUD (custom_rules) =====================

  // 用户自定义规则 ID 段从 100000 起，避开内置规则集的 1000-9999。
  const nextCustomRuleId = () => {
    const ids = customRules.map(r => Number(r.rule_id) || 0);
    const max = ids.length ? Math.max(...ids) : 0;
    return Math.max(100000, max + 1);
  };

  // 把本地 custom_rules 数组持久化（复用 waf-config/save，携带当前配置避免覆盖其它字段）。
  const persistCustomRules = async (rules) => {
    setCrSaving(true);
    try {
      const response = await cdnService.saveWafConfig({
        domain_id: domainData.id,
        enabled_rulesets: enabledRulesets,
        mode: wafConfig.mode || 'active',
        score_threshold: wafConfig.score_threshold || 5,
        paranoia_level: wafConfig.paranoia_level || 1,
        custom_rules: rules,
      });
      if (response.success) {
        setCustomRules(rules);
        message.success('自定义规则已保存');
        return true;
      }
      message.error(response.error || '保存失败');
      return false;
    } catch (error) {
      message.error('保存失败');
      return false;
    } finally {
      setCrSaving(false);
    }
  };

  const handleCreateCustomRule = () => {
    setEditingCrIndex(null);
    crForm.resetFields();
    crForm.setFieldsValue({
      enabled: true,
      collection: 'URI',
      match_type: 'regex',
      pattern: '',
      action_type: 'block',
      score: 5,
      status: 403,
      message: '',
    });
    setCrModalVisible(true);
  };

  const handleEditCustomRule = (rule, index) => {
    setEditingCrIndex(index);
    const m = rule.match || {};
    const a = rule.action || {};
    crForm.setFieldsValue({
      rule_id: rule.rule_id,
      enabled: rule.enabled !== false,
      collection: m.collection || 'URI',
      var: m.var || '',
      match_type: m.type || 'regex',
      pattern: m.pattern || '',
      action_type: a.type || 'block',
      score: a.score ?? 5,
      status: a.status ?? 403,
      message: a.message || '',
    });
    setCrModalVisible(true);
  };

  const handleSaveCustomRule = async () => {
    try {
      const values = await crForm.validateFields();
      const match = {
        type: values.match_type,
        collection: values.collection,
      };
      if (['HEADER', 'COOKIE'].includes(values.collection) && values.var) {
        match.var = values.var;
      }
      if (values.match_type === 'regex') {
        match.pattern = values.pattern;
      }
      const action = {
        type: values.action_type,
        score: values.score ?? 5,
        log: true,
      };
      if (values.action_type === 'block') {
        action.status = values.status || 403;
      }
      if (values.message) action.message = values.message;

      const rule = {
        rule_id: values.rule_id || nextCustomRuleId(),
        phase: 'request',
        enabled: values.enabled !== false,
        match,
        action,
      };

      const next = [...customRules];
      if (editingCrIndex != null) {
        next[editingCrIndex] = rule;
      } else {
        next.push(rule);
      }
      const ok = await persistCustomRules(next);
      if (ok) setCrModalVisible(false);
    } catch (error) {
      if (error && error.errorFields) return; // form validation
      message.error('操作失败');
    }
  };

  const handleDeleteCustomRule = async (index) => {
    const next = customRules.filter((_, i) => i !== index);
    await persistCustomRules(next);
  };

  const handleToggleCustomRule = async (index, enabled) => {
    const next = customRules.map((r, i) => (i === index ? { ...r, enabled } : r));
    await persistCustomRules(next);
  };

  const crCollectionWatch = Form.useWatch('collection', crForm);
  const crMatchTypeWatch = Form.useWatch('match_type', crForm);
  const crActionWatch = Form.useWatch('action_type', crForm);

  // ===================== 防火墙规则 CRUD =====================

  const loadFirewallRules = async () => {
    setFwLoading(true);
    try {
      const response = await cdnService.getFirewallRules({ domain_id: domainData.id });
      if (response.success) {
        setFwRules(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load firewall rules:', error);
      message.error('防火墙规则加载失败');
    } finally {
      setFwLoading(false);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    fwForm.resetFields();
    fwForm.setFieldsValue({
      action: 'block',
      priority: 100,
      enabled: true,
      conditions: [{ collection: 'IP', op: 'in', var: '' }],
      action_config: {},
    });
    setFwModalVisible(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    // 解析 expression 到 conditions
    const conditions = rule.expression?.conditions || [{ collection: 'IP', op: 'in', var: '' }];
    // 限速富参：旧数据仅有 key（ip/country/uri），映射到新的 key_by 维度。
    const actionConfig = { ...(rule.action_config || {}) };
    if (rule.action === 'ratelimit' && actionConfig.key_by == null && actionConfig.key != null) {
      actionConfig.key_by = actionConfig.key;
      delete actionConfig.key;
    }
    fwForm.setFieldsValue({
      name: rule.name,
      description: rule.description,
      action: rule.action,
      priority: rule.priority,
      enabled: rule.enabled,
      conditions: conditions.map(c => ({
        collection: c.collection,
        key: c.key,
        op: c.op,
        var: Array.isArray(c.var) ? c.var.join('\n') : (c.var || ''),
      })),
      action_config: actionConfig,
    });
    setFwModalVisible(true);
  };

  const handleSaveRule = async () => {
    try {
      const values = await fwForm.validateFields();

      // 构建 expression
      const conditions = (values.conditions || []).map(c => {
        let varValue = c.var;
        // 对 in/not_in 运算符，将换行分隔的值转为数组
        if (['in', 'not_in'].includes(c.op) && typeof varValue === 'string') {
          varValue = varValue.split('\n').map(v => v.trim()).filter(v => v);
        }
        const cond = { collection: c.collection, op: c.op, var: varValue };
        if (c.key) cond.key = c.key;
        return cond;
      });

      const expression = {
        op: 'and',
        conditions,
      };

      const payload = {
        domain_id: domainData.id,
        name: values.name,
        description: values.description,
        expression,
        action: values.action,
        action_config: values.action_config || {},
        priority: values.priority || 100,
        enabled: values.enabled !== false,
      };

      let response;
      if (editingRule) {
        response = await cdnService.updateFirewallRule({ id: editingRule.id, ...payload });
      } else {
        response = await cdnService.createFirewallRule(payload);
      }

      if (response.success) {
        message.success(editingRule ? '规则已更新' : '规则已创建');
        setFwModalVisible(false);
        loadFirewallRules();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      if (error.errorFields) return; // form validation
      message.error('操作失败');
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      const response = await cdnService.deleteFirewallRule({ id });
      if (response.success) {
        message.success('规则已删除');
        loadFirewallRules();
      } else {
        message.error(response.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleToggleRule = async (rule, enabled) => {
    try {
      const response = await cdnService.updateFirewallRule({ id: rule.id, enabled });
      if (response.success) {
        message.success(enabled ? '规则已启用' : '规则已禁用');
        loadFirewallRules();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // ===================== WAF 分析 =====================

  const loadAnalytics = async () => {
    if (!analyticsRange || analyticsRange.length < 2) return;
    setAnalyticsLoading(true);
    try {
      const params = {
        domain_id: domainData.id,
        startTime: analyticsRange[0].unix(),
        endTime: analyticsRange[1].unix(),
      };

      const [overviewRes, timelineRes, topRulesRes, topIPsRes] = await Promise.all([
        cdnService.getWafOverview(params),
        cdnService.getWafTimeline(params),
        cdnService.getWafTopRules(params),
        cdnService.getWafTopIPs(params),
      ]);

      if (overviewRes.success) setOverview(overviewRes.data || {});
      if (timelineRes.success) setTimeline(timelineRes.data || []);
      if (topRulesRes.success) setTopRules(topRulesRes.data || []);
      if (topIPsRes.success) setTopIPs(topIPsRes.data || []);
    } catch (error) {
      console.error('Failed to load WAF analytics:', error);
      message.error('WAF 分析数据加载失败');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const renderTimelineChart = () => {
    if (!timelineChartRef.current) return;
    if (timelineChartInstance.current) {
      timelineChartInstance.current.dispose();
    }
    const chart = echarts.init(timelineChartRef.current);
    timelineChartInstance.current = chart;

    // 按 fw_action 分组
    const actionGroups = {};
    timeline.forEach(item => {
      const action = item.fw_action || 'unknown';
      if (!actionGroups[action]) actionGroups[action] = [];
      actionGroups[action].push({
        time: item.time_bucket,
        count: item.count,
      });
    });

    const colorMap = {
      block: '#ff4d4f',
      allow: '#52c41a',
      js_challenge: '#fa8c16',
      managed_challenge: '#faad14',
      log: '#1890ff',
      ratelimit: '#722ed1',
    };

    const series = Object.entries(actionGroups).map(([action, data]) => ({
      name: ACTION_LABELS[action] || action,
      type: 'line',
      smooth: true,
      areaStyle: { opacity: 0.15 },
      data: data.map(d => [d.time, d.count]),
      itemStyle: { color: colorMap[action] || '#666' },
    }));

    chart.setOption({
      tooltip: { trigger: 'axis' },
      legend: { data: series.map(s => s.name), bottom: 0 },
      grid: { left: 50, right: 20, top: 20, bottom: 40 },
      xAxis: { type: 'time' },
      yAxis: { type: 'value', minInterval: 1 },
      series,
    });

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  };

  // ===================== 事件日志 =====================

  const loadLogs = async () => {
    if (!logsRange || logsRange.length < 2) return;
    setLogsLoading(true);
    try {
      const params = {
        domain_id: domainData.id,
        startTime: logsRange[0].unix(),
        endTime: logsRange[1].unix(),
        page: logsPage,
        pageSize: logsPageSize,
        ...logFilters,
      };
      const response = await cdnService.getWafLogs(params);
      if (response.success) {
        setLogs(response.data || []);
        setLogsTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Failed to load WAF logs:', error);
      message.error('WAF 日志加载失败');
    } finally {
      setLogsLoading(false);
    }
  };

  // ===================== 条件摘要 =====================

  const getExpressionSummary = (expression) => {
    if (!expression?.conditions) return '-';
    return expression.conditions.map(c => {
      const val = Array.isArray(c.var) ? c.var.slice(0, 3).join(', ') + (c.var.length > 3 ? '...' : '') : (c.var || '');
      const keyPart = c.key ? `[${c.key}]` : '';
      return `${c.collection}${keyPart} ${c.op} ${val}`;
    }).join(' AND ');
  };

  // ===================== Tab 1: 规则配置 =====================

  const fwRuleColumns = [
    {
      title: '优先级',
      dataIndex: 'priority',
      width: 80,
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: '名称',
      dataIndex: 'name',
      ellipsis: true,
    },
    {
      title: '匹配条件',
      dataIndex: 'expression',
      ellipsis: true,
      render: (expr) => (
        <Tooltip title={getExpressionSummary(expr)}>
          <span style={{ fontSize: 12, color: '#666' }}>{getExpressionSummary(expr)}</span>
        </Tooltip>
      ),
    },
    {
      title: '动作',
      dataIndex: 'action',
      width: 100,
      render: (action) => (
        <Tag color={ACTION_COLORS[action]}>{ACTION_LABELS[action] || action}</Tag>
      ),
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 70,
      render: (enabled, record) => (
        <Switch
          size="small"
          checked={enabled}
          onChange={(v) => handleToggleRule(record, v)}
        />
      ),
    },
    {
      title: '操作',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditRule(record)} />
          <Popconfirm title="确定删除此规则？" onConfirm={() => handleDeleteRule(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 自定义 WAF 评分规则 —— 表格列
  const customRuleColumns = [
    { title: 'ID', dataIndex: 'rule_id', width: 90 },
    {
      title: '匹配',
      key: 'match',
      ellipsis: true,
      render: (_, r) => {
        const m = r.match || {};
        const coll = m.var ? `${m.collection}[${m.var}]` : m.collection;
        const type = (WAF_MATCH_TYPES.find(t => t.value === m.type) || {}).label || m.type;
        return (
          <Tooltip title={m.pattern || type}>
            <span style={{ fontSize: 12, color: '#666' }}>{coll} · {m.type === 'regex' ? m.pattern : type}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '动作',
      key: 'action',
      width: 110,
      render: (_, r) => {
        const a = r.action || {};
        return (
          <Space size={4}>
            <Tag color={a.type === 'block' ? 'red' : 'blue'}>{a.type === 'block' ? '拦截' : '记录'}</Tag>
            <Tag>+{a.score ?? 0}</Tag>
          </Space>
        );
      },
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 70,
      render: (enabled, record, index) => (
        <Switch size="small" checked={enabled !== false} onChange={(v) => handleToggleCustomRule(index, v)} loading={crSaving} />
      ),
    },
    {
      title: '操作',
      width: 100,
      render: (_, record, index) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditCustomRule(record, index)} />
          <Popconfirm title="确定删除此规则？" onConfirm={() => handleDeleteCustomRule(index)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderCustomRulesCard = () => (
    <Card
      title={<Space><ThunderboltOutlined /> {isMobile ? '自定义规则' : '自定义 WAF 规则（评分）'}</Space>}
      extra={
        <Button type="primary" icon={<PlusOutlined />} size={isMobile ? 'small' : 'middle'} onClick={handleCreateCustomRule} disabled={!wafEnabled}>
          新增规则
        </Button>
      }
      size={isMobile ? 'small' : 'default'}
      style={{ marginTop: 24 }}
    >
      <Alert
        message="自定义评分规则命中后按分值累加请求的异常分，达到上方「异常分阈值」即拦截。适合在内置规则集之外补充针对性检测。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Table
        dataSource={customRules.map((r, i) => ({ ...r, __index: i }))}
        columns={isMobile ? customRuleColumns.filter(c => ['ID', '动作', '操作'].includes(c.title)) : customRuleColumns}
        rowKey={(r) => r.rule_id ?? r.__index}
        size="small"
        pagination={false}
        locale={{ emptyText: <Empty description="暂无自定义规则，点击右上角添加" /> }}
      />
    </Card>
  );

  const renderCustomRuleModal = () => (
    <Modal
      title={editingCrIndex != null ? '编辑自定义 WAF 规则' : '新增自定义 WAF 规则'}
      open={crModalVisible}
      onOk={handleSaveCustomRule}
      confirmLoading={crSaving}
      onCancel={() => setCrModalVisible(false)}
      width={isMobile ? '95%' : 640}
      destroyOnClose
    >
      <Form form={crForm} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="rule_id" label="规则 ID" tooltip="留空自动分配（从 100000 起）">
              <InputNumber min={1} style={{ width: '100%' }} placeholder="自动分配" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="enabled" label="启用" valuePropName="checked" initialValue={true}>
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '8px 0' }}>匹配</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="collection" label="检测位置" rules={[{ required: true }]}>
              <Select options={WAF_MATCH_COLLECTIONS} />
            </Form.Item>
          </Col>
          {['HEADER', 'COOKIE'].includes(crCollectionWatch) && (
            <Col span={12}>
              <Form.Item name="var" label={crCollectionWatch === 'HEADER' ? '请求头名' : 'Cookie 名'} rules={[{ required: true, message: '请输入名称' }]}>
                <Input placeholder={crCollectionWatch === 'HEADER' ? '如 X-Forwarded-For' : '如 session'} />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Form.Item name="match_type" label="匹配方式" rules={[{ required: true }]}>
          <Select options={WAF_MATCH_TYPES} />
        </Form.Item>
        {crMatchTypeWatch === 'regex' && (
          <Form.Item name="pattern" label="正则表达式" rules={[{ required: true, message: '请输入正则表达式' }]}>
            <TextArea rows={2} placeholder={'如 (?i)union\\s+select'} autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        )}

        <Divider orientation="left" style={{ margin: '8px 0' }}>动作</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="action_type" label="动作" rules={[{ required: true }]} initialValue="block">
              <Select options={WAF_CUSTOM_ACTIONS} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="score" label="异常分" tooltip="命中累加的异常分，达到阈值即拦截" initialValue={5}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {crActionWatch === 'block' && (
            <Col span={8}>
              <Form.Item name="status" label="拦截状态码" initialValue={403}>
                <InputNumber min={400} max={599} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Form.Item name="message" label="消息" tooltip="记录到 WAF 日志的说明文本">
          <Input placeholder="如 Custom SQLi rule" />
        </Form.Item>
      </Form>
    </Modal>
  );

  const renderRulesTab = () => (
    <div>
      {/* 自定义防火墙规则 */}
      <Card
        title={<Space><FilterOutlined /> 自定义防火墙规则</Space>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} size={isMobile ? 'small' : 'middle'} onClick={handleCreateRule}>
            新增规则
          </Button>
        }
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 24 }}
      >
        <Alert
          message="防火墙规则在页面规则之前执行。匹配到 Allow 规则后将跳过后续 WAF 检查。规则按优先级从小到大依次评估。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={fwRules}
          columns={isMobile ? fwRuleColumns.filter(c => ['name', 'action', '操作'].includes(c.title)) : fwRuleColumns}
          rowKey="id"
          loading={fwLoading}
          size="small"
          pagination={false}
          locale={{ emptyText: <Empty description="暂无防火墙规则，点击右上角添加规则" /> }}
        />
      </Card>

      {/* WAF 预设规则集（现有面板） */}
      <Card
        title={
          <Space>
            <SafetyOutlined />
            {isMobile ? 'WAF规则集' : 'WAF 预设规则集'}
          </Space>
        }
        extra={
          <Switch
            checked={wafEnabled}
            onChange={handleWafToggle}
            loading={wafToggling}
            checkedChildren="启用"
            unCheckedChildren="禁用"
          />
        }
        size={isMobile ? 'small' : 'default'}
      >
        <div style={{ marginBottom: 12, fontSize: 12, color: '#888' }}>
          关闭 WAF 只停用预设规则集和自定义 WAF 规则，上方的防火墙规则仍会执行。
        </div>
        <div style={{ marginBottom: 16 }}>
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            <Tag color="green" icon={<CheckCircleOutlined />}>{enabledRulesets.length} 个已启用</Tag>
            {rulesetsVersion && <Tag color="blue">v{rulesetsVersion}</Tag>}
            {rulesetsDirty && <Tag color="orange">有未保存的更改</Tag>}
            <Button type="primary" danger={rulesetsDirty} onClick={handleSaveConfig} loading={loading} disabled={!wafEnabled} size={isMobile ? 'small' : 'middle'}>保存</Button>
            <Button onClick={loadWafRulesets} icon={<ReloadOutlined />} disabled={loading} size={isMobile ? 'small' : 'middle'}>
              {isMobile ? '刷新' : '刷新规则集'}
            </Button>
          </Space>
        </div>

        {/* CRS 规则集高级设置：paranoia level + 异常分阈值（对齐 wafcrs CrsConfig） */}
        <Card
          size="small"
          type="inner"
          title={<Space><ThunderboltOutlined /> CRS 引擎设置</Space>}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 12]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontSize: 13 }}>
                Paranoia 级别
                <Tooltip title="偏执级别 1-4：级别越高，加载的检测规则越多、检出率越高，但误报也越多。生产建议从 1 开始逐步提升。">
                  <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                </Tooltip>
              </div>
              <Select
                value={wafConfig.paranoia_level || 1}
                onChange={(v) => { setWafConfig({ ...wafConfig, paranoia_level: v }); setRulesetsDirty(true); }}
                disabled={!wafEnabled}
                style={{ width: '100%' }}
                options={[
                  { value: 1, label: 'PL1 · 最少误报（推荐）' },
                  { value: 2, label: 'PL2 · 均衡' },
                  { value: 3, label: 'PL3 · 严格' },
                  { value: 4, label: 'PL4 · 最严格' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontSize: 13 }}>
                异常分阈值
                <Tooltip title="入站异常评分累计达到该阈值即拦截请求。CRS 默认 5，值越低越敏感。">
                  <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                </Tooltip>
              </div>
              <InputNumber
                min={1}
                max={100}
                value={wafConfig.score_threshold || 5}
                onChange={(v) => { setWafConfig({ ...wafConfig, score_threshold: v }); setRulesetsDirty(true); }}
                disabled={!wafEnabled}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontSize: 13 }}>
                检测模式
                <Tooltip title="active=拦截并记录；simulate=仅记录不拦截（演练）；passive=不生效。">
                  <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                </Tooltip>
              </div>
              <Select
                value={wafConfig.mode || 'active'}
                onChange={(v) => { setWafConfig({ ...wafConfig, mode: v }); setRulesetsDirty(true); }}
                disabled={!wafEnabled}
                style={{ width: '100%' }}
                options={[
                  { value: 'active', label: '拦截（active）' },
                  { value: 'simulate', label: '仅记录（simulate）' },
                  { value: 'passive', label: '关闭（passive）' },
                ]}
              />
            </Col>
          </Row>
        </Card>

        {Object.keys(rulesetsByCategory).length === 0 ? (
          <Alert message="暂无可用规则集" description="请稍后重试或联系管理员" type="warning" showIcon />
        ) : (
          <Collapse defaultActiveKey={['injection']} ghost>
            {Object.entries(rulesetsByCategory).map(([category, rulesets]) => (
              <Panel
                header={
                  <Space wrap size={isMobile ? 'small' : 'middle'}>
                    <span style={{ fontSize: isMobile ? '14px' : '18px' }}>{CATEGORY_ICONS[category]}</span>
                    <strong>{CATEGORY_NAMES[category]}</strong>
                    <Tag color="blue">
                      {rulesets.filter(r => enabledRulesets.includes(r.key)).length}/{rulesets.length}
                    </Tag>
                  </Space>
                }
                key={category}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={isMobile ? 'small' : 'middle'}>
                  {rulesets.map(ruleset => (
                    <Card
                      key={ruleset.key}
                      size="small"
                      style={{ borderLeft: `4px solid ${enabledRulesets.includes(ruleset.key) ? '#52c41a' : '#d9d9d9'}` }}
                    >
                      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 8 : 0 }}>
                        <div style={{ flex: 1 }}>
                          <Space direction="vertical" size="small">
                            <Space wrap size="small">
                              <strong style={{ fontSize: isMobile ? '14px' : '16px' }}>{ruleset.name}</strong>
                              <Tag color={SEVERITY_COLORS[ruleset.severity]}>{SEVERITY_NAMES[ruleset.severity]}</Tag>
                            </Space>
                            <div style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>{ruleset.description}</div>
                            <div style={{ fontSize: '12px', color: '#999' }}>
                              规则: {Array.isArray(ruleset.rules) ? ruleset.rules.length : 0} 条
                            </div>
                          </Space>
                        </div>
                        <Switch
                          checked={enabledRulesets.includes(ruleset.key)}
                          onChange={(checked) => handleRulesetToggle(ruleset.key, checked)}
                          disabled={!wafEnabled}
                          checkedChildren="启用"
                          unCheckedChildren="禁用"
                          style={isMobile ? { marginTop: 4 } : undefined}
                        />
                      </div>
                    </Card>
                  ))}
                </Space>
              </Panel>
            ))}
          </Collapse>
        )}
      </Card>

      {/* 自定义 WAF 评分规则（custom_rules） */}
      {renderCustomRulesCard()}
    </div>
  );

  // ===================== Tab 2: WAF 分析 =====================

  const topRuleColumns = [
    { title: '规则 ID', dataIndex: 'fw_rule_id', ellipsis: true },
    { title: '规则名称', dataIndex: 'fw_rule_name', ellipsis: true },
    { title: '类型', dataIndex: 'fw_rule_type', render: v => <Tag>{v}</Tag> },
    { title: '动作', dataIndex: 'fw_action', render: v => <Tag color={ACTION_COLORS[v]}>{ACTION_LABELS[v] || v}</Tag> },
    { title: '命中数', dataIndex: 'hits', sorter: (a, b) => a.hits - b.hits },
    { title: '独立 IP', dataIndex: 'unique_ips' },
  ];

  const topIPColumns = [
    { title: 'IP 地址', dataIndex: 'client_ip' },
    { title: '国家', dataIndex: 'country_code', render: v => (v ? <CountryLabel code={v}>{v}</CountryLabel> : '-') },
    { title: '事件数', dataIndex: 'events', sorter: (a, b) => a.events - b.events },
    { title: '拦截数', dataIndex: 'blocks' },
    { title: '触发规则数', dataIndex: 'rules_triggered' },
  ];

  const renderAnalyticsTab = () => (
    <Spin spinning={analyticsLoading}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <RangePicker
            showTime
            value={analyticsRange}
            onChange={setAnalyticsRange}
            size={isMobile ? 'small' : 'middle'}
            presets={[
              { label: '最近1小时', value: [dayjs().subtract(1, 'hour'), dayjs()] },
              { label: '最近24小时', value: [dayjs().subtract(24, 'hour'), dayjs()] },
              { label: '最近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
              { label: '最近30天', value: [dayjs().subtract(30, 'day'), dayjs()] },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={loadAnalytics} size={isMobile ? 'small' : 'middle'}>刷新</Button>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="总事件数" value={overview.total_events || 0} prefix={<BarChartOutlined />} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="拦截数" value={overview.blocked || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<StopOutlined />} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="攻击 IP 数" value={overview.unique_ips || 0} prefix={<WarningOutlined />} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="触发规则数" value={overview.unique_rules || 0} prefix={<FilterOutlined />} /></Card>
          </Col>
        </Row>

        <Card title="事件时间线" size="small">
          {timeline.length > 0 ? (
            <div ref={timelineChartRef} style={{ width: '100%', height: 300 }} />
          ) : (
            <Empty description="暂无数据" />
          )}
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Top 触发规则" size="small">
              <Table
                dataSource={topRules}
                columns={isMobile ? topRuleColumns.filter(c => ['规则名称', '动作', '命中数'].includes(c.title)) : topRuleColumns}
                rowKey={(r, i) => i}
                size="small"
                pagination={false}
                scroll={{ x: isMobile ? undefined : 600 }}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Top 攻击 IP" size="small">
              <Table
                dataSource={topIPs}
                columns={isMobile ? topIPColumns.filter(c => ['IP 地址', '事件数', '拦截数'].includes(c.title)) : topIPColumns}
                rowKey={(r, i) => i}
                size="small"
                pagination={false}
              />
            </Card>
          </Col>
        </Row>
      </Space>
    </Spin>
  );

  // ===================== Tab 3: 事件日志 =====================

  const logColumns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      width: 160,
      render: v => v || '-',
    },
    { title: 'IP', dataIndex: 'client_ip', width: 130 },
    { title: '方法', dataIndex: 'method', width: 60 },
    { title: 'URI', dataIndex: 'uri', ellipsis: true },
    {
      title: '规则',
      dataIndex: 'fw_rule_name',
      ellipsis: true,
      render: (v, r) => v || r.fw_rule_id || '-',
    },
    {
      title: '动作',
      dataIndex: 'fw_action',
      width: 90,
      render: v => <Tag color={ACTION_COLORS[v]}>{ACTION_LABELS[v] || v}</Tag>,
    },
    { title: '国家', dataIndex: 'country_code', width: 80, render: v => (v ? <CountryLabel code={v}>{v}</CountryLabel> : '-') },
  ];

  const renderLogsTab = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <RangePicker
          showTime
          value={logsRange}
          onChange={setLogsRange}
          size={isMobile ? 'small' : 'middle'}
          presets={[
            { label: '最近1小时', value: [dayjs().subtract(1, 'hour'), dayjs()] },
            { label: '最近24小时', value: [dayjs().subtract(24, 'hour'), dayjs()] },
            { label: '最近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
          ]}
        />
        <Select
          placeholder="动作类型"
          allowClear
          style={{ width: 120 }}
          size={isMobile ? 'small' : 'middle'}
          onChange={v => setLogFilters(f => ({ ...f, action: v }))}
          options={Object.entries(ACTION_LABELS).map(([k, v]) => ({ value: k, label: v }))}
        />
        <Input
          placeholder="IP 地址"
          allowClear
          style={{ width: 140 }}
          size={isMobile ? 'small' : 'middle'}
          onPressEnter={e => setLogFilters(f => ({ ...f, client_ip: e.target.value || undefined }))}
        />
        <Input
          placeholder="URI"
          allowClear
          style={{ width: 160 }}
          size={isMobile ? 'small' : 'middle'}
          onPressEnter={e => setLogFilters(f => ({ ...f, uri: e.target.value || undefined }))}
        />
        <Button icon={<SearchOutlined />} onClick={loadLogs} size={isMobile ? 'small' : 'middle'}>查询</Button>
      </div>

      <Table
        dataSource={logs}
        columns={isMobile ? logColumns.filter(c => ['时间', 'IP', '动作'].includes(c.title)) : logColumns}
        rowKey={(r, i) => r.request_id || i}
        loading={logsLoading}
        size="small"
        pagination={{
          current: logsPage,
          pageSize: logsPageSize,
          total: logsTotal,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setLogsPage(page);
            setLogsPageSize(pageSize);
          },
        }}
        scroll={{ x: isMobile ? undefined : 900 }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ padding: 8, fontSize: 12 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}><strong>规则 ID:</strong> {record.fw_rule_id}</Col>
                <Col span={12}><strong>规则类型:</strong> {record.fw_rule_type}</Col>
                <Col span={12}><strong>消息:</strong> {record.fw_message}</Col>
                {record.fw_detail && <Col span={24}><strong>引擎详情:</strong> {record.fw_detail}</Col>}
                <Col span={12}><strong>匹配键:</strong> {record.fw_matched_key || '-'}</Col>
                <Col span={24}><strong>匹配值:</strong> {record.fw_matched_value || '-'}</Col>
                <Col span={24}><strong>User-Agent:</strong> {record.http_user_agent || '-'}</Col>
                <Col span={12}><strong>Host:</strong> {record.host}</Col>
                <Col span={12}><strong>状态码:</strong> {record.status}</Col>
              </Row>
            </div>
          ),
        }}
      />
    </Space>
  );

  // ===================== 规则编辑 Modal =====================

  const selectedAction = Form.useWatch('action', fwForm);
  // 限速富参：按维度/计量联动表单。
  const rlKeyBy = Form.useWatch(['action_config', 'key_by'], fwForm);
  const rlMetric = Form.useWatch(['action_config', 'metric'], fwForm);

  const renderRuleModal = () => (
      <Modal
        title={editingRule ? '编辑防火墙规则' : '新增防火墙规则'}
        open={fwModalVisible}
        onOk={handleSaveRule}
        onCancel={() => setFwModalVisible(false)}
        width={isMobile ? '95%' : 720}
        destroyOnClose
      >
        <Form form={fwForm} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
                <Input placeholder="例如：封禁恶意 IP" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="优先级">
                <InputNumber min={1} max={9999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="描述">
            <Input placeholder="可选描述" />
          </Form.Item>

          <Divider orientation="left" style={{ margin: '12px 0' }}>匹配条件</Divider>

          <Form.List name="conditions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} align="top" style={{ marginBottom: 8 }}>
                    <Col span={5}>
                      <Form.Item {...restField} name={[name, 'collection']} rules={[{ required: true }]}>
                        <Select options={COLLECTIONS} placeholder="数据" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...restField} name={[name, 'key']}>
                        <Input placeholder="键(可选)" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...restField} name={[name, 'op']} rules={[{ required: true }]}>
                        <Select options={OPERATORS} placeholder="运算符" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={9}>
                      <Form.Item {...restField} name={[name, 'var']} rules={[{ required: true, message: '请输入值' }]}>
                        <TextArea rows={1} placeholder="值（多个值换行分隔）" size="small" autoSize={{ minRows: 1, maxRows: 3 }} />
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const c = getFieldValue(['conditions', name]) || {};
                          return c.collection === 'COUNTRY' ? <CountryFlagList value={c.var} style={{ marginTop: -16, marginBottom: 8 }} /> : null;
                        }}
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      {fields.length > 1 && (
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                      )}
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ collection: 'IP', op: 'eq', var: '' })} icon={<PlusOutlined />} size="small">
                  添加条件
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left" style={{ margin: '12px 0' }}>动作</Divider>

          <Form.Item name="action" label="动作类型" rules={[{ required: true }]}>
            <Select options={Object.entries(ACTION_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
          </Form.Item>

          {selectedAction === 'block' && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['action_config', 'status']} label="状态码" initialValue={403}>
                  <InputNumber min={400} max={599} />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item name={['action_config', 'message']} label="消息">
                  <Input placeholder="Access Denied" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {selectedAction === 'ratelimit' && (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="限速富参：按所选维度在滑动窗口内计数，达到上限后执行超限处置。"
              />
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={['action_config', 'requests']}
                    label={rlMetric === 'bandwidth' ? '速率上限(字节/秒)' : '请求数上限'}
                    initialValue={100}
                    rules={[{ required: true, message: '请输入上限' }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['action_config', 'ttl']} label="窗口(秒)" initialValue={60} rules={[{ required: true, message: '请输入窗口' }]}>
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['action_config', 'metric']} label="计量方式" initialValue="requests" tooltip="requests=按请求次数；bandwidth=按响应字节数（防大流量下载）">
                    <Select options={[
                      { value: 'requests', label: '请求次数' },
                      { value: 'bandwidth', label: '带宽（字节）' },
                    ]} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={rlKeyBy === 'header' || rlKeyBy === 'cookie' ? 8 : 12}>
                  <Form.Item name={['action_config', 'key_by']} label="限速维度" initialValue="ip" tooltip="按哪个维度聚合计数">
                    <Select options={[
                      { value: 'ip', label: 'IP 地址' },
                      { value: 'header', label: '请求头' },
                      { value: 'cookie', label: 'Cookie' },
                      { value: 'uri', label: 'URI 路径' },
                      { value: 'country', label: '国家' },
                    ]} />
                  </Form.Item>
                </Col>
                {(rlKeyBy === 'header' || rlKeyBy === 'cookie') && (
                  <Col span={8}>
                    <Form.Item
                      name={['action_config', 'key_name']}
                      label={rlKeyBy === 'header' ? '请求头名' : 'Cookie 名'}
                      rules={[{ required: true, message: '请输入名称' }]}
                    >
                      <Input placeholder={rlKeyBy === 'header' ? '如 X-Api-Key' : '如 session_id'} />
                    </Form.Item>
                  </Col>
                )}
                <Col span={rlKeyBy === 'header' || rlKeyBy === 'cookie' ? 8 : 12}>
                  <Form.Item name={['action_config', 'scope']} label="作用域" initialValue="domain" tooltip="按维度：每个 IP（或所选维度）各自计数；按维度+路径：再按 URI 路径分开；合计：本规则所有请求共用一个计数">
                    <Select options={[
                      { value: 'domain', label: '按维度' },
                      { value: 'path', label: '按维度+路径' },
                      { value: 'global', label: '合计' },
                    ]} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name={['action_config', 'on_exceed']} label="超限处置" initialValue="block" tooltip="到达上限后对后续请求执行的动作">
                <Select options={[
                  { value: 'block', label: '拦截（429）' },
                  { value: 'challenge', label: '人机质询' },
                  { value: 'log', label: '仅记录' },
                  { value: 'tarpit', label: '延迟（tarpit）' },
                ]} />
              </Form.Item>
            </>
          )}

          {(selectedAction === 'js_challenge' || selectedAction === 'managed_challenge') && (
            <Form.Item label="通过后有效期"
              extra={selectedAction === 'managed_challenge' ? '使用「访问控制 → 5秒盾」的 captcha.la 密钥；未填写时使用平台验证码' : undefined}>
              <span>取「访问控制 → 5秒盾 → 通行证有效期」</span>
            </Form.Item>
          )}
        </Form>
      </Modal>
  );

  // ===================== Tab: 访问控制（专用安全区） =====================
  // 专用防盗链(referers) / 地区封禁(geo) / UA 名单(uafilter) + 访问鉴权(auth)。
  // 存入 domains.settings.security，下发映射到 Rust referers/geo/ua_filter/auth 专用模块，
  // 与「自定义防火墙规则」表分离（收敛），避免与通用表达式规则冲突。

  const UA_MATCH_TYPES = [
    { value: 'keyword', label: '包含(子串)' },
    { value: 'wildcard', label: '通配(* ?)' },
    { value: 'regex', label: '正则' },
    { value: 'exact', label: '精确' },
    { value: 'prefix', label: '前缀' },
    { value: 'suffix', label: '后缀' },
  ];

  const linesJoin = (arr) => (Array.isArray(arr) ? arr.join('\n') : (arr || ''));

  const securityToForm = (sec) => {
    sec = sec || {};
    const h = sec.hotlink || {};
    const g = sec.geo || {};
    const u = sec.ua_filter || {};
    const a = sec.auth || {};
    return {
      hotlink: {
        enabled: !!h.enabled,
        allow_empty: !!h.allow_empty,
        allow_same_domain: !!h.allow_same_domain,
        allow_domains: linesJoin(h.allow_domains),
        deny_domains: linesJoin(h.deny_domains),
        extensions: linesJoin(h.extensions),
      },
      geo: {
        enabled: !!g.enabled,
        deny_countries: linesJoin(g.deny_countries),
        allow_countries: linesJoin(g.allow_countries),
        deny_regions: linesJoin(g.deny_regions),
        allow_regions: linesJoin(g.allow_regions),
        deny_asns: linesJoin(g.deny_asns),
        allow_asns: linesJoin(g.allow_asns),
      },
      ua_filter: {
        enabled: !!u.enabled,
        default_action: u.default_action || 'allow',
        empty_action: u.empty_action || '',
        deny_status: u.deny_status || 0,
        case_sensitive: !!u.case_sensitive,
        rules: (u.rules || []).map((r) => ({
          action: r.action || 'deny',
          match_type: r.match_type || 'keyword',
          pattern: r.pattern || '',
        })),
      },
      auth: {
        enabled: !!a.enabled,
        combine: a.combine || 'and',
        ip_allow: linesJoin(a.ip_allow),
        ip_deny: linesJoin(a.ip_deny),
        methods: (a.methods || []).map((m) => {
          if (m.type === 'basic') {
            return {
              type: 'basic',
              realm: m.realm || '',
              users_text: (m.users || []).map((x) => `${x.username || ''}:${x.password || ''}`).join('\n'),
            };
          }
          if (m.type === 'token') {
            return { type: 'token', param: m.param || 'token', location: m.location || 'query', tokens: linesJoin(m.tokens) };
          }
          if (m.type === 'sign_url') {
            return {
              type: 'sign_url',
              sign_type: m.sign_type || 'a',
              key: m.key || '',
              param: m.param || '',
              valid_window: m.valid_window || 0,
              skew: m.skew || 0,
            };
          }
          return { type: m.type };
        }),
      },
      uam: {
        mode: (sec.uam || {}).mode || 'off',
        min_wait: (sec.uam || {}).min_wait ?? 5,
        difficulty: (sec.uam || {}).difficulty ?? 15,
        clearance_ttl: (sec.uam || {}).clearance_ttl ?? 1800,
        captcha: {
          provider: ((sec.uam || {}).captcha || {}).provider || 'captchala',
          site_key: ((sec.uam || {}).captcha || {}).site_key || '',
          secret: ((sec.uam || {}).captcha || {}).secret || '',
          verify_url: ((sec.uam || {}).captcha || {}).verify_url || '',
        },
        whitelist: {
          ips: linesJoin(((sec.uam || {}).whitelist || {}).ips),
          static_exts: linesJoin(((sec.uam || {}).whitelist || {}).static_exts),
          bot_asns: linesJoin(((sec.uam || {}).whitelist || {}).bot_asns),
        },
      },
      protect: {
        enabled: !!(sec.protect || {}).enabled,
        max_conns_per_ip: (sec.protect || {}).max_conns_per_ip ?? 0,
        slowloris: {
          enabled: !!((sec.protect || {}).slowloris || {}).enabled,
          min_body_rate: ((sec.protect || {}).slowloris || {}).min_body_rate ?? 0,
          window_secs: ((sec.protect || {}).slowloris || {}).window_secs ?? 0,
          grace_secs: ((sec.protect || {}).slowloris || {}).grace_secs ?? 0,
          max_idle_secs: ((sec.protect || {}).slowloris || {}).max_idle_secs ?? 0,
          max_body_time: ((sec.protect || {}).slowloris || {}).max_body_time ?? 0,
        },
      },
    };
  };

  const buildSecurityPayload = (v) => {
    v = v || {};
    const h = v.hotlink || {};
    const g = v.geo || {};
    const u = v.ua_filter || {};
    const a = v.auth || {};
    return {
      hotlink: {
        enabled: !!h.enabled,
        allow_empty: !!h.allow_empty,
        allow_same_domain: !!h.allow_same_domain,
        allow_domains: h.allow_domains || '',
        deny_domains: h.deny_domains || '',
        extensions: h.extensions || '',
      },
      geo: {
        enabled: !!g.enabled,
        deny_countries: g.deny_countries || '',
        allow_countries: g.allow_countries || '',
        deny_regions: g.deny_regions || '',
        allow_regions: g.allow_regions || '',
        deny_asns: g.deny_asns || '',
        allow_asns: g.allow_asns || '',
      },
      ua_filter: {
        enabled: !!u.enabled,
        default_action: u.default_action || 'allow',
        empty_action: u.empty_action || '',
        deny_status: u.deny_status || 0,
        case_sensitive: !!u.case_sensitive,
        rules: (u.rules || [])
          .filter((r) => r && (r.pattern || '').trim())
          .map((r) => ({ action: r.action || 'deny', match_type: r.match_type || 'keyword', pattern: r.pattern })),
      },
      auth: {
        enabled: !!a.enabled,
        combine: a.combine || 'and',
        ip_allow: a.ip_allow || '',
        ip_deny: a.ip_deny || '',
        methods: (a.methods || [])
          .map((m) => {
            if (!m || !m.type) return null;
            if (m.type === 'basic') {
              const users = (m.users_text || '')
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean)
                .map((l) => {
                  const idx = l.indexOf(':');
                  return idx >= 0
                    ? { username: l.slice(0, idx), password: l.slice(idx + 1) }
                    : { username: l, password: '' };
                });
              return { type: 'basic', users, realm: m.realm || '' };
            }
            if (m.type === 'token') {
              return { type: 'token', param: m.param || 'token', location: m.location || 'query', tokens: m.tokens || '' };
            }
            if (m.type === 'sign_url') {
              return {
                type: 'sign_url',
                sign_type: m.sign_type || 'a',
                key: m.key || '',
                param: m.param || '',
                valid_window: m.valid_window || 0,
                skew: m.skew || 0,
              };
            }
            return null;
          })
          .filter(Boolean),
      },
      uam: (() => {
        const um = v.uam || {};
        const wl = um.whitelist || {};
        const toArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
        return {
          mode: um.mode || 'off',
          min_wait: um.min_wait || 0,
          difficulty: um.difficulty || 0,
          clearance_ttl: um.clearance_ttl || 0,
          captcha: {
            provider: (um.captcha || {}).provider || 'captchala',
            site_key: (um.captcha || {}).site_key || '',
            secret: (um.captcha || {}).secret || '',
            verify_url: (um.captcha || {}).verify_url || '',
          },
          whitelist: {
            ips: toArr(wl.ips),
            static_exts: toArr(wl.static_exts),
            bot_asns: toArr(wl.bot_asns).map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n)),
          },
        };
      })(),
      protect: (() => {
        const p = v.protect || {};
        const sl = p.slowloris || {};
        return {
          enabled: !!p.enabled,
          max_conns_per_ip: p.max_conns_per_ip || 0,
          slowloris: {
            enabled: !!sl.enabled,
            min_body_rate: Number(sl.min_body_rate) || 0,
            window_secs: sl.window_secs || 0,
            grace_secs: sl.grace_secs || 0,
            max_idle_secs: sl.max_idle_secs || 0,
            max_body_time: sl.max_body_time || 0,
          },
        };
      })(),
    };
  };

  const handleSaveSecurity = async () => {
    setSecSaving(true);
    try {
      await secForm.validateFields();
      // 用 getFieldsValue(true) 取完整 store（含条件渲染未挂载、但已由 setFieldsValue 预填的字段，
      // 如 mode!==captcha 时的 captcha.site_key/secret），避免保存把已存验证码密钥清空（#12）。
      const security = buildSecurityPayload(secForm.getFieldsValue(true));
      const response = await cdnService.updateDomain({ id: domainData.id, security });
      if (response.success) {
        message.success('访问控制配置已保存');
        reloadDomain && reloadDomain();
      } else {
        message.error(response.error || '保存失败');
      }
    } catch (error) {
      if (error && error.errorFields) return;
      message.error('保存失败');
    } finally {
      setSecSaving(false);
    }
  };

  const renderSecurityTab = () => (
    <Form form={secForm} layout="vertical" size={isMobile ? 'small' : 'middle'}>
      <Alert
        message="访问控制为该域名的专用安全模块，独立于上方「自定义防火墙规则」。启用后按各自开关在边缘生效。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 专用防盗链 → Rust referers */}
      <Card
        title={<Space><StopOutlined /> 防盗链（Referer 名单）</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['hotlink', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren="启用" unCheckedChildren="禁用" /></Form.Item>}
      >
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Form.Item name={['hotlink', 'allow_empty']} label="允许空 Referer" valuePropName="checked" tooltip="直接访问 / 隐私模式常见空 Referer"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name={['hotlink', 'allow_same_domain']} label="允许同域" valuePropName="checked" tooltip="Referer 主机与站点同域或其子域时放行"><Switch /></Form.Item>
          </Col>
        </Row>
        <Form.Item name={['hotlink', 'allow_domains']} label="白名单域名" tooltip="每行一个。支持精确域名、通配(*.example.com / img.*)、正则(以 ~ 前缀)。非空时 Referer 必须命中其一">
          <TextArea rows={3} placeholder={'example.com\n*.example.com\n~^https?://cdn\\.trusted\\.com/'} />
        </Form.Item>
        <Form.Item name={['hotlink', 'deny_domains']} label="黑名单域名" tooltip="每行一个，命中即拦截（优先于白名单）">
          <TextArea rows={2} placeholder={'thief.com\n*.badsite.net'} />
        </Form.Item>
        <Form.Item name={['hotlink', 'extensions']} label="仅保护后缀" tooltip="留空=所有路径生效；填后缀（每行一个，如 .jpg .mp4）则仅这些资源受防盗链约束">
          <TextArea rows={2} placeholder={'.jpg\n.png\n.mp4'} />
        </Form.Item>
      </Card>

      {/* 专用地区封禁 → Rust geo */}
      <Card
        title={<Space><FilterOutlined /> 地区 / ASN 封禁</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['geo', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren="启用" unCheckedChildren="禁用" /></Form.Item>}
      >
        <Alert message="黑名单命中即封；白名单非空时仅放行名单内（已解析 IP）。国家用 ISO alpha-2（CN/US），省份用 subdivision ISO（如 ENG），ASN 用数字。逐维 AND。" type="warning" showIcon style={{ marginBottom: 12 }} />
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'deny_countries']} label="国家黑名单"><TextArea rows={2} placeholder={'CN\nRU'} /></Form.Item>
            <Form.Item noStyle shouldUpdate={(p, c) => p.geo?.deny_countries !== c.geo?.deny_countries}>{({ getFieldValue }) => <CountryFlagList value={getFieldValue(['geo', 'deny_countries'])} style={{ marginTop: -16, marginBottom: 12 }} />}</Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'allow_countries']} label="国家白名单"><TextArea rows={2} placeholder={'US\nGB'} /></Form.Item>
            <Form.Item noStyle shouldUpdate={(p, c) => p.geo?.allow_countries !== c.geo?.allow_countries}>{({ getFieldValue }) => <CountryFlagList value={getFieldValue(['geo', 'allow_countries'])} style={{ marginTop: -16, marginBottom: 12 }} />}</Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'deny_regions']} label="省/州黑名单"><TextArea rows={2} placeholder={'ENG'} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'allow_regions']} label="省/州白名单"><TextArea rows={2} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'deny_asns']} label="ASN 黑名单"><TextArea rows={2} placeholder={'15169\n13335'} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'allow_asns']} label="ASN 白名单"><TextArea rows={2} /></Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 专用 UA 名单 → Rust ua_filter */}
      <Card
        title={<Space><SafetyOutlined /> User-Agent 名单</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['ua_filter', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren="启用" unCheckedChildren="禁用" /></Form.Item>}
      >
        <Row gutter={16}>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'default_action']} label="默认动作" tooltip="无规则命中时：allow=黑名单模式；deny=白名单模式">
              <Select options={[{ value: 'allow', label: '放行' }, { value: 'deny', label: '拦截' }]} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'empty_action']} label="空 UA" tooltip="防去 UA 头绕过">
              <Select options={[{ value: '', label: '跟随规则' }, { value: 'allow', label: '放行' }, { value: 'deny', label: '拦截' }]} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'deny_status']} label="拦截状态码" tooltip="0=默认 403">
              <InputNumber min={0} max={599} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'case_sensitive']} label="大小写敏感" valuePropName="checked"><Switch /></Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" style={{ margin: '8px 0' }}>规则（自上而下，首条命中即定判）</Divider>
        <Form.List name={['ua_filter', 'rules']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="top" style={{ marginBottom: 8 }}>
                  <Col span={5}>
                    <Form.Item {...restField} name={[name, 'action']} rules={[{ required: true }]}>
                      <Select size="small" options={[{ value: 'deny', label: '拦截' }, { value: 'allow', label: '放行' }]} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item {...restField} name={[name, 'match_type']} rules={[{ required: true }]}>
                      <Select size="small" options={UA_MATCH_TYPES} />
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item {...restField} name={[name, 'pattern']} rules={[{ required: true, message: '请输入模式' }]}>
                      <Input size="small" placeholder="如 sqlmap / *bot* / ^curl/" />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add({ action: 'deny', match_type: 'keyword', pattern: '' })}>添加 UA 规则</Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* 访问鉴权 → Rust auth */}
      <Card
        title={<Space><CheckCircleOutlined /> 访问鉴权</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['auth', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren="启用" unCheckedChildren="禁用" /></Form.Item>}
      >
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name={['auth', 'combine']} label="多方法组合" tooltip="and=全部通过；or=任一通过">
              <Select options={[{ value: 'and', label: '全部通过 (AND)' }, { value: 'or', label: '任一通过 (OR)' }]} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['auth', 'ip_allow']} label="IP 白名单" tooltip="精确 IP 或 CIDR，每行一个；非空时仅放行名单内"><TextArea rows={2} placeholder={'10.0.0.0/8\n1.2.3.4'} /></Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['auth', 'ip_deny']} label="IP 黑名单" tooltip="命中即 403（优先于白名单）"><TextArea rows={2} /></Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" style={{ margin: '8px 0' }}>鉴权方法</Divider>
        <Form.List name={['auth', 'methods']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => {
                const mtype = authMethodsWatch?.[name]?.type;
                return (
                  <Card key={key} size="small" style={{ marginBottom: 8 }} bodyStyle={{ paddingBottom: 0 }}>
                    <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
                      <Col flex="auto">
                        <Form.Item {...restField} name={[name, 'type']} label="类型" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Select
                            options={[
                              { value: 'basic', label: 'HTTP Basic' },
                              { value: 'token', label: '参数 Token' },
                              { value: 'sign_url', label: '签名 URL (A/B/C/D)' },
                            ]}
                            placeholder="选择鉴权方式"
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                      </Col>
                    </Row>

                    {mtype === 'basic' && (
                      <>
                        <Form.Item {...restField} name={[name, 'realm']} label="Realm"><Input placeholder="Restricted" /></Form.Item>
                        <Form.Item {...restField} name={[name, 'users_text']} label="用户（每行 user:pass）" rules={[{ required: true, message: '至少一个用户' }]}>
                          <TextArea rows={2} placeholder={'alice:s3cret\nbob:passw0rd'} />
                        </Form.Item>
                      </>
                    )}

                    {mtype === 'token' && (
                      <Row gutter={8}>
                        <Col xs={12} sm={6}>
                          <Form.Item {...restField} name={[name, 'param']} label="参数名"><Input placeholder="token" /></Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Form.Item {...restField} name={[name, 'location']} label="位置">
                            <Select options={[{ value: 'query', label: 'Query' }, { value: 'header', label: 'Header' }]} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item {...restField} name={[name, 'tokens']} label="允许的 Token（每行一个）" rules={[{ required: true, message: '至少一个 token' }]}>
                            <TextArea rows={2} />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {mtype === 'sign_url' && (
                      <>
                        <Row gutter={8}>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'sign_type']} label="签名类型">
                              <Select options={[
                                { value: 'a', label: 'A (query md5)' },
                                { value: 'b', label: 'B (path md5)' },
                                { value: 'c', label: 'C (path md5)' },
                                { value: 'd', label: 'D (HMAC-SHA256)' },
                              ]} />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'param']} label="参数名" tooltip="A 默认 auth_key，D 默认 sign"><Input placeholder="auth_key / sign" /></Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'valid_window']} label="有效期(秒)" tooltip="B/C 型，0=默认 1800"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'skew']} label="时钟偏移(秒)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
                          </Col>
                        </Row>
                        <Form.Item {...restField} name={[name, 'key']} label="私钥 / Key" rules={[{ required: true, message: '签名密钥不能为空' }]}>
                          <Input.Password placeholder="签名私钥（为空则全部拒绝）" />
                        </Form.Item>
                      </>
                    )}
                  </Card>
                );
              })}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ type: 'basic', location: 'query', sign_type: 'a' })}>添加鉴权方法</Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* 5秒盾 / 攻击模式 → Rust uam */}
      <Card
        title={<Space><ThunderboltOutlined /> 5秒盾 / 攻击模式（UAM）</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
      >
        <Alert message="对可疑流量下发人机验证。自适应=按压力自动质询；受攻击=对所有访客先质询（5秒盾）；强制验证码=用第三方验证码。通过后颁发通行证在有效期内免验。" type="warning" showIcon style={{ marginBottom: 12 }} />
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Form.Item name={['uam', 'mode']} label="模式" tooltip="off=关闭；adaptive=按 CC 压力自动质询（需开启连接防护）；under_attack=全员5秒盾；captcha=强制验证码">
              <Select options={[
                { value: 'off', label: '关闭' },
                { value: 'adaptive', label: '自适应' },
                { value: 'under_attack', label: '受攻击（5秒盾）' },
                { value: 'captcha', label: '强制验证码' },
              ]} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['uam', 'min_wait']} label="最短等待(秒)" tooltip="质询页最短停留时间，拦截秒过脚本" extra="建议 ≥5 秒(名副其实的「5秒盾」);过低防护极弱。留空/填 0 采用节点安全默认。">
              <InputNumber min={0} max={60} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['uam', 'difficulty']} label="难度" tooltip="工作量证明（PoW）难度，值越大客户端计算越耗时" extra="建议 ≥15;难度过低 PoW 近乎瞬解,防护极弱。留空/填 0 采用节点安全默认。">
              <InputNumber min={0} max={32} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['uam', 'clearance_ttl']} label="通行证有效期(秒)" tooltip="通过验证后在此时长内免于再次质询">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        {(uamModeWatch === 'captcha' || fwRules.some((r) => r.action === 'managed_challenge')) && (
          <>
            <Divider orientation="left" style={{ margin: '8px 0' }}>验证码（Captcha）</Divider>
            <Row gutter={16}>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'provider']} label="提供商" initialValue="captchala"
                  extra="边缘目前仅支持 captcha.la 托管验证">
                  <Select options={[
                    { value: 'captchala', label: 'captcha.la（托管验证）' },
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'site_key']} label="Site Key"><Input placeholder="站点公钥" /></Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'secret']} label="Secret"><Input.Password placeholder="服务端密钥" /></Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'verify_url']} label="校验地址" tooltip="留空则用提供商默认校验端点"><Input placeholder="https://..." /></Form.Item>
              </Col>
            </Row>
          </>
        )}
        <Divider orientation="left" style={{ margin: '8px 0' }}>白名单（跳过质询）</Divider>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name={['uam', 'whitelist', 'ips']} label="IP / CIDR 白名单" tooltip="每行一个，命中直接放行不质询"><TextArea rows={3} placeholder={'10.0.0.0/8\n1.2.3.4'} /></Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['uam', 'whitelist', 'static_exts']} label="静态资源后缀" tooltip="这些后缀的请求跳过质询，每行一个（受攻击/强制验证码模式下不生效）"><TextArea rows={3} placeholder={'.jpg\n.css\n.js'} /></Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['uam', 'whitelist', 'bot_asns']} label="放行 Bot ASN" tooltip="搜索引擎等可信 ASN（数字），每行一个（受攻击/强制验证码模式下不生效）"><TextArea rows={3} placeholder={'15169\n8075'} /></Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 连接防护 → Rust protect */}
      <Card
        title={<Space><SafetyOutlined /> 连接防护（Slowloris）</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['protect', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren="启用" unCheckedChildren="禁用" /></Form.Item>}
      >
        <Alert message="限制单 IP 并发连接并识别慢速请求（Slowloris），缓解连接耗尽型攻击。" type="warning" showIcon style={{ marginBottom: 12 }} />
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Form.Item name={['protect', 'max_conns_per_ip']} label="单 IP 最大并发连接" tooltip="0=不限"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" style={{ margin: '8px 0' }}>慢速攻击防护（Slowloris）</Divider>
        <Row gutter={16}>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'enabled']} label="启用" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'min_body_rate']} label="最低体速率(B/s)" tooltip="请求体传输速率低于此值视为慢速攻击"><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'window_secs']} label="采样窗口(秒)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'grace_secs']} label="宽限期(秒)" tooltip="连接建立后允许慢速的初始宽限时间"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'max_idle_secs']} label="最大空闲(秒)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'max_body_time']} label="请求体最长(秒)" tooltip="接收完整请求体的最长时间上限"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          </Col>
        </Row>
      </Card>

      <div style={{ textAlign: 'right' }}>
        <Button type="primary" onClick={handleSaveSecurity} loading={secSaving}>保存访问控制</Button>
      </div>
    </Form>
  );

  // ===================== 主布局 =====================

  const tabItems = [
    {
      key: 'rules',
      label: <span><FilterOutlined /> 规则配置</span>,
      children: renderRulesTab(),
    },
    {
      key: 'access',
      label: <span><SafetyOutlined /> 访问控制</span>,
      children: renderSecurityTab(),
    },
    {
      key: 'analytics',
      label: <span><BarChartOutlined /> WAF 分析</span>,
      children: renderAnalyticsTab(),
    },
    {
      key: 'logs',
      label: <span><UnorderedListOutlined /> 事件日志</span>,
      children: renderLogsTab(),
    },
  ];

  return (
    <div style={{ padding: isMobile ? '12px 0' : 0 }}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size={isMobile ? 'small' : 'middle'}
      />
      {renderRuleModal()}
      {renderCustomRuleModal()}
    </div>
  );
}
